import { RefObject, useEffect } from 'react'

type ClickOutsideParams = {
  targetRef: RefObject<HTMLElement>
  callback: () => void
  closeOnTarget: boolean
  blockClick?: boolean
  background?: HTMLElement | typeof document
}

const isNode = (e: EventTarget | null): e is Node =>
  Boolean(e && 'nodeType' in e)

/**
 * Hook attach and cleanup event listeners that used to track click events
 * outside of target element.
 * @param targetRef {Object} - react targetRef with DOM element as a targetRef.current.
 * @param callback {function} - to be called once outside click happens
 * @param closeOnTarget {Boolean} - flag that is used to treat target element
 * @param blockClick {Boolean} - flag that is used to block callback call
 * @param background {Object} - background event listened would be attached
 * during outside click event as not part of targetRef element
 */
const useOutsideClickHook = ({
  targetRef,
  callback,
  closeOnTarget = false,
  blockClick = false,
  background = document,
  // background = document,
}: ClickOutsideParams) => {
  useEffect(() => {
    if (!background) return
    function handleClick(e: Event) {
      if (blockClick) return
      if (closeOnTarget && e.target === targetRef.current) {
        callback()
      }

      if (
        targetRef.current &&
        isNode(e.target) &&
        !targetRef.current.contains(e.target)
      ) {
        callback()
      }
    }

    setTimeout(() => {
      background.addEventListener('click', handleClick, true)
    })

    return () => {
      background.removeEventListener('click', handleClick)
    }
  }, [callback, closeOnTarget, targetRef, blockClick, background])
}

export default useOutsideClickHook
